import {
  Button,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { ArrowLeftIcon } from 'lucide-react';
import { InterviewPersonaCard } from '../InterviewPersonaCard/InterviewPersonaCard';
import { useTranslation } from 'react-i18next';

interface InterviewPersonaItem {
  id: string;
  firstName: string;
  age: string;
  segmentName: string;
  segmentDescription?: string;
  personaBrief?: string;
  personaTitle?: string;
  photoAssetId?: string;
  tags: string[];
}

export interface InterviewPersonasPanelProps {
  personas: InterviewPersonaItem[];
  onPersonaSelect: (persona: InterviewPersonaItem) => void;
  onBack: () => void;
  isLoading?: boolean;
}

export function InterviewPersonasPanel({
  personas,
  onPersonaSelect,
  onBack,
  isLoading,
}: InterviewPersonasPanelProps) {
  const { t } = useTranslation('audiences');
  const { t: tCommon } = useTranslation('common');
  const backLabel = t('audienceInterview.interviewPersonasPanel.backToAudiences');

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg bg-background">
      <div className="flex items-center gap-2 border-b border-border px-3 py-3">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-lg" aria-label={backLabel} onClick={onBack}>
                <ArrowLeftIcon className="size-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{backLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <span className="text-base font-medium leading-6 text-foreground">
          {t('audienceInterview.interviewPersonasPanel.personasOverview')}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <h2 className="pb-10 pt-4 text-center text-xl font-medium leading-6 tracking-[-0.8px] text-foreground">
          {t('audienceInterview.interviewPersonasPanel.whoWouldYouLikeTo')}
        </h2>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {personas.map((persona) => (
              <InterviewPersonaCard
                key={persona.id}
                name={persona.firstName || tCommon('components.sidePanel.unknown')}
                age={persona.age}
                title={persona.segmentName || tCommon('components.personaSelector.unknownSegment')}
                tags={persona.tags}
                description={persona.personaBrief ?? persona.segmentDescription}
                photoAssetId={persona.photoAssetId}
                onClick={() => onPersonaSelect(persona)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
